import React, { useEffect, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, message, Form } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import InputCard from "../../../../../Components/Cards/InputCard";
import {
  getAdminFuelTypeById,
  updateFuelType,
} from "../../../../../API/Admin/SystemConfiguration/FuelTypes";
import "../style.css";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const EditFuelType = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const passedId = location.state?.id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No fuel type selected. Please open this page from the Fuel Types list.",
      );
      return;
    }

    const fetchFuelType = async () => {
      setLoading(true);
      try {
        const res = await getAdminFuelTypeById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          form.setFieldsValue({
            name: data.name,
            status: data.status,
          });
        } else {
          message.error(res?.data?.message || "Failed to load fuel type.");
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel type."),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFuelType();
  }, [passedId, form]);

  const goToList = () => {
    navigate("/system-configuration", {
      state: { activeTab: "Fuel Types" },
    });
  };

  const fields = [
    {
      name: "name",
      label: "Fuel Type Name",
      type: "text",
      placeholder: "Enter Fuel Type Name",
      rules: [{ required: true, message: "Fuel type name is required" }],
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: "Select Status",
      options: STATUS_OPTIONS,
      rules: [{ required: true, message: "Status is required" }],
    },
  ];

  const handleSubmit = async () => {
    if (!passedId) {
      message.error("No fuel type selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill all fields in the form.");
      return;
    }

    setSaving(true);
    try {
      const res = await updateFuelType(passedId, {
        name: values.name,
        status: values.status,
      });

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "Fuel type updated successfully!"),
        );
        goToList();
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update fuel type."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update fuel type."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fuel-type-form">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={goToList}>
          Back
        </Button>
      </div>
      <div className="title">Edit Fuel Type</div>

      <Form form={form} layout="vertical" disabled={loading}>
        <div className="input-cards-container">
          <InputCard title="General Information" fields={fields} />
        </div>

        <div className="btns-row">
          <Button onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditFuelType;
